import {tx, ptx, usePageText} from '../lib/page-text';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '../lib/localized-router';
import { api } from '../lib/api-client';
import { Modal, EmptyState } from '../components/ui';
import { EmbedIcon, PlusIcon, StreamIcon, KeyIcon } from '../components/icons';

interface FeatureDef { code: string; label: string; description: string; group: string }
interface ProjectSummary {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  features: string[];
  region: string;
  status: string;
  apiKeyCount: number;
  channelCount: number;
  createdAt: string;
}

export function ProjectsPage() {
  usePageText();
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);

  const projects = useQuery<ProjectSummary[]>({
    queryKey: ['projects'],
    queryFn: async () => (await api.get('/me/projects')).data,
  });
  const catalog = useQuery<FeatureDef[]>({
    queryKey: ['feature-catalog'],
    queryFn: async () => (await api.get('/me/projects/features/catalog')).data,
  });

  return (
    <div>
      <div className="row-between" style={{ marginBottom: 28 }}>
        <div>
          <h1 className="h1">{tx("Projects")}</h1>
          <p className="subtitle" style={{ marginBottom: 0 }}>{tx("One project per app or use case. Toggle features, mint API keys, and integrate.")}</p>
        </div>
        <button className="btn lg" onClick={() => setShowCreate(true)}>{ptx("{v0} New project", {v0: (<PlusIcon size={16}/>)})}</button>
      </div>

      {projects.isLoading && <p className="muted">{tx("Loading…")}</p>}

      {projects.data && projects.data.length === 0 && (
        <EmptyState
          icon={<EmbedIcon/>}
          title={tx("No projects yet")}
          body={tx("Create your first project to bundle the features you need (chat, live streaming, conferencing, calls). Each project gets its own API keys.")}
          action={<button className="btn" onClick={() => setShowCreate(true)}>{ptx("{v0} New project", {v0: (<PlusIcon/>)})}</button>}
        />
      )}

      {projects.data && projects.data.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
          {projects.data.map((p) => (
            <Link key={p.id} to={`/projects/${p.id}`} className="card project-card" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="row-between" style={{ alignItems: 'flex-start', marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>{p.name}</div>
                  <div className="muted mono" style={{ fontSize: 12 }}>{p.slug}</div>
                </div>
                <span className="badge">{p.region}</span>
              </div>
              {p.description && (
                <p className="muted" style={{ fontSize: 13, marginBottom: 12 }}>{p.description}</p>
              )}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12, minHeight: 24 }}>
                {p.features.length === 0
                  ? <span className="muted" style={{ fontSize: 12 }}>{tx("No features enabled — click to configure")}</span>
                  : p.features.slice(0, 5).map((f) => (
                      <span key={f} className="badge">{catalog.data?.find((c) => c.code === f)?.label ?? f}</span>
                    ))}
                {p.features.length > 5 && <span className="badge">+{p.features.length - 5}</span>}
              </div>
              <div className="row-between muted" style={{ fontSize: 12 }}>
                <span>{ptx("{icon} API keys: {count}", {icon: <KeyIcon/>, count: p.apiKeyCount})}</span>
                <span>{ptx("{icon} Channels: {count}", {icon: <StreamIcon/>, count: p.channelCount})}</span>
              </div>
            </Link>
          ))}
        </div>
      )}

      {showCreate && catalog.data && (
        <CreateProjectModal
          features={catalog.data}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); qc.invalidateQueries({ queryKey: ['projects'] }); }}
        />
      )}
    </div>
  );
}

function CreateProjectModal({
  features,
  onClose,
  onCreated,
}: {
  features: FeatureDef[];
  onClose: () => void;
  onCreated: () => void;
}) {
  usePageText();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState<string[]>(['chat']);
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () => (await api.post('/me/projects', { name, description: description || undefined, features: selected })).data,
    onSuccess: onCreated,
    onError: (e: any) => setErr(e?.response?.data?.message ?? 'Could not create'),
  });

  function toggle(code: string) {
    setSelected((cur) => cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code]);
  }

  // Group features for clean UI
  const groups: Record<string, FeatureDef[]> = {};
  for (const f of features) (groups[f.group] = groups[f.group] || []).push(f);

  return (
    <Modal onClose={onClose}>
      <h2>{tx("New project")}</h2>
      <p className="modal-subtitle">{tx("Each project is an isolated container with its own API keys and feature set.")}</p>

      <label className="label">{tx("Name")}</label>
      <input className="input" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder={tx("e.g. Kardopal customer chat")}/>

      <div style={{ height: 14 }}/>
      <label className="label">{ptx("Description {v0}", {v0: (<span className="muted">{tx("(optional)")}</span>)})}</label>
      <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder={tx("What is this project for?")}/>

      <div style={{ height: 18 }}/>
      <label className="label">{tx("Features")}</label>
      <p className="muted" style={{ fontSize: 12, marginTop: -4, marginBottom: 8 }}>{tx("You can change these later.")}</p>

      {Object.entries(groups).map(([group, items]) => (
        <div key={group} style={{ marginBottom: 10 }}>
          <div className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{group}</div>
          <div className="card" style={{ padding: '4px 16px' }}>
            {items.map((f) => (
              <div key={f.code} className="toggle-row">
                <div className="label-block">
                  <span className="name">{f.label}</span>
                  <span className="desc">{f.description}</span>
                </div>
                <button type="button" className={`toggle ${selected.includes(f.code) ? 'on' : ''}`} onClick={() => toggle(f.code)}/>
              </div>
            ))}
          </div>
        </div>
      ))}

      {err && <div className="error">{err}</div>}
      <div className="row-between" style={{ marginTop: 20 }}>
        <button className="btn outline" onClick={onClose}>{tx("Cancel")}</button>
        <button className="btn" disabled={!name || create.isPending} onClick={() => create.mutate()}>
          {create.isPending ? tx("Creating…") : tx("Create project")}
        </button>
      </div>
    </Modal>
  );
}
